import { useState } from 'react';
import { useMutation } from 'react-query';
import { api } from '../../services/api';
import Button from '../ui/Button';
import { ErrorNotice } from '../ui/States';
import { StatusMark } from '../ui/Pill';
import { errorMessage, inputClass } from '../../lib/programs';
import { dayLabel } from '../../lib/events';

/** A finished event is read-only after 90 days; a super admin can unlock it for a week, with a reason. */
export default function LockBanner({
  eventId,
  lockedFrom,
  isSuperAdmin,
  refresh,
}: {
  eventId: string;
  lockedFrom: string | null;
  isSuperAdmin: boolean;
  refresh: () => void;
}) {
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState('');
  const unlock = useMutation(async () => api.post(`/events/${eventId}/unlock`, { reason }), {
    onSuccess: () => {
      setAsking(false);
      setReason('');
      refresh();
    },
  });
  return (
    <div className="rounded-sm bg-accent-tint text-accent px-4 py-3 text-[15px] leading-[22px]">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="flex gap-[10px]">
          <span className="mt-1">
            <StatusMark mark="info" />
          </span>
          <span>
            <strong>Read-only.</strong> This event ended long ago
            {lockedFrom ? ` (locked since ${dayLabel(lockedFrom)})` : ''}, so its record cannot be changed.
          </span>
        </span>
        {isSuperAdmin && !asking ? (
          <Button size="sm" variant="quiet" onClick={() => setAsking(true)}>
            Unlock for 7 days
          </Button>
        ) : null}
      </div>
      {asking ? (
        <div className="flex flex-wrap gap-2 mt-3">
          <input
            aria-label="Why does it need to change?"
            className={`${inputClass} flex-1 min-w-[260px]`}
            placeholder="Why does it need to change? (kept in the audit trail)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <Button size="sm" disabled={reason.trim().length < 5 || unlock.isLoading} onClick={() => unlock.mutate()}>
            Unlock
          </Button>
          <Button size="sm" variant="quiet" onClick={() => setAsking(false)}>
            Cancel
          </Button>
        </div>
      ) : null}
      {unlock.error ? (
        <div className="mt-2">
          <ErrorNotice>{errorMessage(unlock.error, 'Could not unlock the event.')}</ErrorNotice>
        </div>
      ) : null}
    </div>
  );
}
